import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { intersection } from 'lodash';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useFeesContext } from 'sonata-platform-components';
import { Checkbox, Input, RadioButtonsGroup, Selector, Switch, Toast } from 'sonata-react-components';
import { BaseCurrencyPositionEnum } from 'sonata-static-storage/lib/enum/baseCurrency.enum';
import { normalize } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { PRICING_MODELS_BY_MEDIA_TYPE as pricingModels } from 'sonata-static-storage/lib/samples/pricingModels.samples';
import { BidModelsMediaTypeEnum, PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import { MediaTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { usePricingModel } from '../PricingModel.context';
import { PricingModelMainSectionStyled } from './PricingModelMainSection.styled';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { NumberProp, StringProp } from '../pricingModel.types';

type Props = {
  id: string;
};

function PricingModelMainSection({ id }: Props) {
  const { formatMessage } = useIntl();
  const { minimumBidPrices } = useFeesContext();
  const {
    currency,
    dynamicBidPrice,
    errors,
    isValidBusinessLogic,
    mediaType,
    pricingModel: currentPricingModel,
    pricingModelConfig,
    setBidPrice,
    setDynamicBidPrice,
    setPricingModel,
    setProp,
    user,
    userRoles
  } = usePricingModel();
  const dynamicBidPriceEnabled = checkUserResourcePermissions('dynamicBidPrice', user, userRoles);
  const cpxOnlyMediaSelectorPermission = checkUserResourcePermissions('cpxOnlyMediaSelector', user, userRoles);

  const { bidModels, cpxOnlyMedia } = pricingModelConfig;

  let availablePricingModels: PricingModelEnum[] = user.pricingModels[mediaType];
  if (mediaType === MediaTypeEnum.hybrid) {
    availablePricingModels = intersection(
      ...Object.values(user.pricingModels as Record<BidModelsMediaTypeEnum, PricingModelEnum[]>)
    );
  }
  availablePricingModels = availablePricingModels.filter(userPM =>
    mediaType === MediaTypeEnum.hybrid ? false : pricingModels[mediaType]?.[userPM]?.active
  );

  const currentPricingModelConfig =
    pricingModels[mediaType === MediaTypeEnum.hybrid ? MediaTypeEnum.openRtb : mediaType]?.[currentPricingModel];

  const eventTypeOptions: SelectorOptionPropsInterface[] = (currentPricingModelConfig?.eventTypes || []).map(eT => ({
    id: eT,
    label: formatMessage({
      defaultMessage: eT,
      id: `pricingModel.mainSection.eventTypes.${eT}`
    })
  }));

  const currentEventTypeSelected = eventTypeOptions.filter(
    opt =>
      currentPricingModelConfig?.eventAttribute &&
      opt.id === pricingModelConfig[currentPricingModelConfig!.eventAttribute! as StringProp]
  );

  const handleBidPriceChange = (value: string, bidModelMediaType?: BidModelsMediaTypeEnum) => {
    setBidPrice(new BigNumber(value).decimalPlaces(4).toNumber(), bidModelMediaType);
  };

  const handleExpectedMetricValueChange = (value: string) => {
    setProp(currentPricingModelConfig!.ctrInputAttribute! as NumberProp, parseFloat(value) || 0);
  };

  const handleTypeEventChange = (option: { id: string }) => {
    setProp(currentPricingModelConfig!.eventAttribute! as StringProp, option.id);
  };

  const minBidPrices = bidModels.map(bidModel => {
    const bidModelMediaType = bidModel.mediaType as 'openRtb' | 'pmp';
    return minimumBidPrices?.[bidModelMediaType as keyof typeof BidModelsMediaTypeEnum] || 0;
  });

  const getMinBidPriceFormated = (indexBidModel = 0): string => {
    if (!bidModels[indexBidModel]?.mediaType) {
      return '-';
    }
    const minPrice = [normalize(minBidPrices[indexBidModel], 2, undefined), currency.currencySymbol];
    if (currency.currencyPosition === BaseCurrencyPositionEnum.left) {
      minPrice.reverse();
    }
    return minPrice.join(' ');
  };

  const getBidPrice = (index: number): number => {
    const baseBidPrice = bidModels[index]?.bid?.price;
    const bidModelMediaType = bidModels[index]?.mediaType as 'openRtb' | 'pmp';
    const pricingModel = bidModels[index]?.bid.pricingModel as keyof typeof PricingModelEnum;
    return pricingModels[bidModelMediaType]?.[pricingModel]?.perMil ? baseBidPrice * 1000 : baseBidPrice;
  };

  const getCurrentPricingModelConfigTooltip = (
    pricingModel: PricingModelEnum,
    formatMessage: (messageDescriptor: { id: string; defaultMessage: string }) => string
  ) => {
    switch (pricingModel) {
      case PricingModelEnum['f-cpm']:
        return formatMessage({
          defaultMessage:
            'Fixed Cost Per Mille. A guaranteed, flat rate paid per 1,000 impressions. Unlike standard CPM, which fluctuates constantly in real-time bidding auctions, F-CPM is usually negotiated directly between an advertiser and a publisher to lock in a specific price.',
          id: 'pricingModel.pricingModels.f-cpm.tooltip'
        });
      case PricingModelEnum['d-cpm']:
        return formatMessage({
          defaultMessage:
            'Dynamic Cost Per Mille (Cost Per 1,000 impressions). The value set is a maximum bid; Sonata bids up to it, and the effective CPM at the end of the campaign ranges from zero to that maximum. The impressions obtained vary with supply and demand on the ad exchanges.',
          id: 'pricingModel.pricingModels.d-cpm.tooltip'
        });
      case PricingModelEnum.ppc:
        return formatMessage({
          defaultMessage:
            'Cost Per Click. You pay only when a user actively clicks on your advertisement, regardless of how many times the ad was displayed',
          id: 'pricingModel.pricingModels.ppc.tooltip'
        });
      case PricingModelEnum.ppv:
        return formatMessage({
          defaultMessage:
            'Cost Per View. Specifically for video advertising; you pay only when a user watches a minimum duration of your video or interacts with it.',
          id: 'pricingModel.pricingModels.ppv.tooltip'
        });
      case PricingModelEnum.ppal:
        return formatMessage({
          defaultMessage:
            'Cost Per Action. You pay only when a user completes a specific goal (a highly valuable action, such as making a purchase, submitting a lead form, or signing up for a trial.)',
          id: 'pricingModel.pricingModels.ppal.tooltip'
        });
      case PricingModelEnum.ppi:
        return formatMessage({
          defaultMessage:
            'Cost Per Install. You pay only when a user clicks the ad and successfully downloads and installs your application.',
          id: 'pricingModel.pricingModels.ppi.tooltip'
        });
      case PricingModelEnum.ppe:
        return formatMessage({
          defaultMessage:
            'Cost Per Engagement. You pay only when a user actively engages with the ad unit. An "engagement" could mean expanding a banner, swiping through a carousel, playing an in-ad mini-game, or answering a poll.',
          id: 'pricingModel.pricingModels.ppe.tooltip'
        });
      case PricingModelEnum.pppv:
        return formatMessage({
          defaultMessage:
            'Cost Per Page View. A step further than Cost Per Click (CPC). You pay only when a user clicks your ad and your destination website actually fully loads and is viewed. This protects your budget from accidental clicks or users who bounce before the site renders.',
          id: 'pricingModel.pricingModels.pppv.tooltip'
        });
      case PricingModelEnum.ppsv:
        return formatMessage({
          defaultMessage:
            'Cost Per Second Viewed. You pay based on the actual duration (measured in seconds) that a user spends viewing your video or listening to your audio ad, ensuring you only pay for engaged time.',
          id: 'pricingModel.pricingModels.ppsv.tooltip'
        });
      default:
        break;
    }
  };

  // Until the user toggles DBP manually, it follows the user's auto-DBP setting and the business logic
  const isDynamicBidPriceManualSet = Boolean(bidModels[0]?.bid?.dynamicBidPriceManualSet);
  const shouldHaveDynamicBidPriceEnabled = Boolean(user.dynamicBidPriceEnabled && isValidBusinessLogic);
  const isDynamicBidPriceActive = isDynamicBidPriceManualSet ? dynamicBidPrice : shouldHaveDynamicBidPriceEnabled;

  useEffect(() => {
    if (dynamicBidPriceEnabled && !isDynamicBidPriceManualSet && dynamicBidPrice !== shouldHaveDynamicBidPriceEnabled) {
      setDynamicBidPrice(shouldHaveDynamicBidPriceEnabled, false);
    }
  }, [dynamicBidPriceEnabled, isDynamicBidPriceManualSet, dynamicBidPrice, shouldHaveDynamicBidPriceEnabled]);

  return (
    <PricingModelMainSectionStyled className="pricingModelMainSection" id={id}>
      {availablePricingModels.length !== 0 && (
        <div className="pricingModelMainSection__modelSelector">
          <RadioButtonsGroup
            id={`pricingModelSelector-${id}`}
            items={availablePricingModels.map(pricingModel => ({
              id: pricingModel,
              text: formatMessage({
                defaultMessage: pricingModel,
                id: `pricingModel.pricingModels.${pricingModel}`
              }),
              tooltip: {
                content: getCurrentPricingModelConfigTooltip(pricingModel, formatMessage),
                icon: 'fa-regular fa-circle-info',
                place: 'top'
              }
            }))}
            onChange={value => setPricingModel(value as PricingModelEnum)}
            value={currentPricingModel}
          />
        </div>
      )}
      <div
        className={cn('pricingModelMainSection__modelConfig', {
          'pricingModelMainSection__modelConfig--center': bidModels.length > 1
        })}
      >
        <div className="pricingModelMainSection__bidPriceContainer">
          <Switch>
            <Switch.Case condition={mediaType === MediaTypeEnum.hybrid}>
              <Input
                description={`${formatMessage(
                  {
                    defaultMessage: 'Min Price: {amount}',
                    id: 'pricingModel.mainSection.minPrice'
                  },
                  {
                    amount: getMinBidPriceFormated(0)
                  }
                )} ${errors?.bidModels?.[0]?.bid?.price?.message || ''}`}
                id={`bidPriceInput-open-${id}`}
                inputAdditionalProps={{
                  min: minBidPrices[0]
                }}
                inputStatus={errors?.bidModels?.[0]?.bid?.price ? 'wrong' : 'default'}
                label={formatMessage({
                  defaultMessage: 'Open',
                  id: 'pricingModel.mainSection.openBidPriceLabel'
                })}
                name="bidPriceInput-open"
                numberFormat="currency"
                numberOfDecimals={4}
                onChange={value => handleBidPriceChange(value as string, BidModelsMediaTypeEnum.openRtb)}
                shape="squareUI"
                type="number"
                value={getBidPrice(0)}
              />
              <Input
                description={`${formatMessage(
                  {
                    defaultMessage: 'Min Price: {amount}',
                    id: 'pricingModel.mainSection.minPrice'
                  },
                  {
                    amount: getMinBidPriceFormated(1)
                  }
                )} ${errors?.bidModels?.[1]?.bid?.price?.message || ''}`}
                id={`bidPriceInput-premium-${id}`}
                inputAdditionalProps={{
                  min: minBidPrices[1]
                }}
                inputStatus={errors?.bidModels?.[1]?.bid?.price ? 'wrong' : 'default'}
                label={formatMessage({
                  defaultMessage: 'Premium',
                  id: 'pricingModel.mainSection.premiumBidPriceLabel'
                })}
                name="bidPriceInput-premium"
                numberFormat="currency"
                numberOfDecimals={4}
                onChange={value => handleBidPriceChange(value as string, BidModelsMediaTypeEnum.pmp)}
                shape="squareUI"
                type="number"
                value={getBidPrice(1)}
              />
            </Switch.Case>
            <Switch.Case condition={mediaType !== MediaTypeEnum.hybrid}>
              <Input
                currencyConfig={currency}
                description={`${formatMessage(
                  {
                    defaultMessage: 'Min Price: {amount}',
                    id: 'pricingModel.mainSection.minPrice'
                  },
                  {
                    amount: getMinBidPriceFormated()
                  }
                )} ${errors?.bidModels?.[0]?.bid?.price?.message || ''}`}
                id={`bidPriceInput-premium-${id}`}
                inputAdditionalProps={{
                  min: minBidPrices[0]
                }}
                inputStatus={errors?.bidModels?.[0]?.bid?.price ? 'wrong' : 'default'}
                label={formatMessage({
                  defaultMessage: 'Per',
                  id: `pricingModel.mainSection.infoLabel.${currentPricingModel}`
                })}
                name="bidPriceInput-premium"
                numberFormat="currency"
                numberOfDecimals={4}
                onChange={value => handleBidPriceChange(value as string)}
                shape="squareUI"
                type="number"
                value={getBidPrice(0)}
              />
            </Switch.Case>
          </Switch>
        </div>
        <Switch>
          <Switch.Case condition={[PricingModelEnum.ppal, PricingModelEnum.ppv].includes(currentPricingModel)}>
            <Selector
              className="pricingModelMainSection__metricSelector"
              description={errors?.[currentPricingModelConfig!.eventAttribute!]?.message}
              label={formatMessage({
                defaultMessage: 'Counts only when',
                id: 'pricingModel.mainSection.eventTypeLabel'
              })}
              onChange={handleTypeEventChange}
              options={eventTypeOptions}
              selectorStatus={errors?.[currentPricingModelConfig!.eventAttribute!] ? 'wrong' : 'default'}
              type="simple"
              value={currentEventTypeSelected}
            />
          </Switch.Case>
        </Switch>
        <Switch>
          <Switch.Case
            condition={[
              PricingModelEnum.ppal,
              PricingModelEnum.ppc,
              PricingModelEnum.ppe,
              PricingModelEnum.ppi,
              PricingModelEnum.ppv,
              PricingModelEnum.ppsv,
              PricingModelEnum.pppv
            ].includes(currentPricingModel)}
          >
            <div className="pricingModelMainSection__expectedMetricValue">
              <Input
                description={errors?.[currentPricingModelConfig!.ctrInputAttribute!]?.message}
                id={`expected-metric-value-${id}`}
                inlineLabel
                inputAdditionalProps={{
                  min: 0
                }}
                inputStatus={errors?.[currentPricingModelConfig!.ctrInputAttribute!] ? 'wrong' : 'default'}
                label={formatMessage({
                  defaultMessage: 'Expected Metric',
                  id: `pricingModel.mainSection.expectedMetricValueLabels.${currentPricingModel}`
                })}
                name="expected-metric-value"
                numberFormat="percentage"
                numberOfDecimals={2}
                onChange={val => handleExpectedMetricValueChange(val as string)}
                shape="squareUI"
                type="number"
                {...(Boolean(
                  [
                    PricingModelEnum.ppal,
                    PricingModelEnum.ppc,
                    PricingModelEnum.ppe,
                    PricingModelEnum.ppi,
                    PricingModelEnum.ppv,
                    PricingModelEnum.ppsv,
                    PricingModelEnum.pppv
                  ].includes(currentPricingModel)
                ) && {
                  tooltipText: formatMessage({
                    defaultMessage: 'Expected Metric',
                    id: `pricingModel.mainSection.expectedMetricValueTooltip.${currentPricingModel}`
                  })
                })}
                {...(Boolean(currentPricingModelConfig?.ctrInputAttribute) && {
                  value: pricingModelConfig[currentPricingModelConfig!.ctrInputAttribute! as NumberProp]
                })}
              />
            </div>
          </Switch.Case>
        </Switch>
        <div className="pricingModelMainSection__otherOptions">
          {cpxOnlyMediaSelectorPermission && (
            <div className="pricingModelMainSection__mediaOnlySection">
              <Checkbox
                active={cpxOnlyMedia}
                id={`mediaOnly-${id}`}
                labelText={formatMessage({
                  defaultMessage: 'Price refers to media only',
                  id: 'pricingModel.mainSection.mediaOnlyLabel'
                })}
                onClickAction={(_, checked) => setProp('cpxOnlyMedia', checked)}
                type="switch"
              />
            </div>
          )}
          {dynamicBidPriceEnabled && (
            <div className="pricingModelMainSection__dynamicBidPriceConfig">
              <Checkbox
                active={isDynamicBidPriceActive}
                id={`dynamicBidPrice-${id}`}
                labelText={formatMessage({
                  defaultMessage: 'Enable Dynamic Bid Price',
                  id: 'pricingModel.mainSection.dynamicBidPriceLabel'
                })}
                onClickAction={(_, checked) => setDynamicBidPrice(checked)}
                type="switch"
              />
            </div>
          )}
        </div>
        {dynamicBidPrice && !isValidBusinessLogic && (
          <Toast
            autoClose={false}
            className="pricingModelMainSection__dynamicBidPriceConfigToast"
            closeButton={false}
            icon="fal fa-exclamation-triangle"
            text={formatMessage({
              defaultMessage: 'Current business logic does not match conditions for DBP.',
              id: 'pricingModel.mainSection.dynamicBidPriceErrorMessage'
            })}
            type="warning"
          />
        )}
      </div>
    </PricingModelMainSectionStyled>
  );
}

export default PricingModelMainSection;
